import "@/App.css";
import { lazy, Suspense, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Home as HomeIcon, ClipboardList, HardHat, FileText, Receipt, Repeat,
  Tags, Inbox, FolderOpen, Settings, MessageSquare, UserCog, HardHat as HardHatIcon, UsersRound,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import SiteLayout from "@/components/site/SiteLayout";
import { OctobreRose } from "@/components/site/OctobreRose";
import AppLayout from "@/components/app/AppLayout";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Home from "@/pages/public/Home";
import Presentation from "@/pages/public/Presentation";
import Prestations from "@/pages/public/Prestations";
import { ForgotPassword, ResetPassword } from "@/pages/public/PasswordReset";
import { PaymentSuccess, PaymentCancel } from "@/pages/public/PaymentResult";
import Tarifs from "@/pages/public/Tarifs";
import ServicePage from "@/pages/public/ServicePage";
import NotFound from "@/pages/public/NotFound";
import { RedirectOr } from "@/data/redirects";
import DemandePage from "@/pages/public/DemandePage";
import { MentionsLegales, Confidentialite, Cgv, Cookies } from "@/pages/public/Legal";
import LegalRequest from "@/pages/public/LegalRequest";
import { CookieBanner } from "@/components/site/CookieBanner";
import Login from "@/pages/public/Login";
import AuthCallback from "@/pages/public/AuthCallback";
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Calendrier = lazy(() => import("@/pages/admin/Calendrier"));
const Commercial = lazy(() => import("@/pages/admin/Commercial"));
const Documents = lazy(() => import("@/pages/admin/Documents"));
const Parametres = lazy(() => import("@/pages/admin/Parametres"));
const adminResource = (name) => lazy(() => import("@/pages/admin/Resources").then((m) => ({ default: m[name] })));
const Clients = adminResource("Clients"), Logements = adminResource("Logements"), Interventions = adminResource("Interventions"), Chantiers = adminResource("Chantiers");
const Abonnements = adminResource("Abonnements"), AdminFormules = adminResource("Formules"), Demandes = adminResource("Demandes"), Equipes = adminResource("Equipes");
const EquipeInterventions = lazy(() => import("@/pages/equipe/Equipe").then((m) => ({ default: m.EquipeInterventions })));
const EquipeChantiers = lazy(() => import("@/pages/equipe/Equipe").then((m) => ({ default: m.EquipeChantiers })));
const ClientHome = lazy(() => import("@/pages/client/ClientHome"));
const ClientInterventions = lazy(() => import("@/pages/client/ClientInterventions"));
const ClientFactures = lazy(() => import("@/pages/client/ClientFactures"));
const ClientDocuments = lazy(() => import("@/pages/client/ClientDocuments"));
const ClientMessages = lazy(() => import("@/pages/client/ClientMessages"));
const ClientCompte = lazy(() => import("@/pages/client/ClientCompte"));

const ADMIN_NAV = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/admin/calendrier", label: "Calendrier", icon: CalendarDays, id: "calendrier" },
  { to: "/admin/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/admin/logements", label: "Logements", icon: HomeIcon, id: "logements" },
  { to: "/admin/interventions", label: "Interventions", icon: ClipboardList, id: "interventions" },
  { to: "/admin/chantiers", label: "Chantiers", icon: HardHat, id: "chantiers" },
  { to: "/admin/equipes", label: "Équipes", icon: UsersRound, id: "equipes" },
  { to: "/admin/devis", label: "Devis", icon: FileText, id: "devis" },
  { to: "/admin/factures", label: "Factures & paiements", icon: Receipt, id: "factures" },
  { to: "/admin/abonnements", label: "Abonnements", icon: Repeat, id: "abonnements" },
  { to: "/admin/formules", label: "Formules & tarifs", icon: Tags, id: "formules" },
  { to: "/admin/demandes", label: "Demandes", icon: Inbox, id: "demandes" },
  { to: "/admin/documents", label: "Documents", icon: FolderOpen, id: "documents" },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings, id: "parametres" },
];

const CLIENT_NAV = [
  { to: "/espace-client", end: true, label: "Accueil", icon: LayoutDashboard, id: "client-home" },
  { to: "/espace-client/interventions", label: "Rendez-vous & interventions", icon: CalendarDays, id: "client-interventions" },
  { to: "/espace-client/factures", label: "Devis & factures", icon: Receipt, id: "client-factures" },
  { to: "/espace-client/documents", label: "Documents & photos", icon: FolderOpen, id: "client-documents" },
  { to: "/espace-client/messages", label: "Contacter", icon: MessageSquare, id: "client-messages" },
  { to: "/espace-client/compte", label: "Mon compte", icon: UserCog, id: "client-compte" },
];

const EQUIPE_NAV = [
  { to: "/equipe", end: true, label: "Mes interventions", icon: ClipboardList, id: "equipe-interventions" },
  { to: "/equipe/chantiers", label: "Mes chantiers", icon: HardHatIcon, id: "equipe-chantiers" },
  { to: "/equipe/compte", label: "Mon compte", icon: UserCog, id: "equipe-compte" },
];

function AppRouter() {
  const location = useLocation();
  useLayoutEffect(() => {
    const priv = /^\/(admin|espace-client|equipe|connexion|mot-de-passe-oublie|reinitialiser-mot-de-passe|paiement)/.test(location.pathname);
    document.querySelector('meta[name="robots"]')?.setAttribute("content", priv ? "noindex, nofollow" : "index, follow");
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/a-propos" element={<Presentation />} />
        <Route path="/prestations" element={<Prestations />} />
        <Route path="/tarifs-conciergerie-brest" element={<Tarifs />} />
        <Route path="/surveillance-maison-brest" element={<ServicePage slug="surveillance" key="surveillance" />} />
        <Route path="/remise-cles-brest" element={<ServicePage slug="cles" key="cles" />} />
        <Route path="/petits-travaux-brest" element={<ServicePage slug="travaux" key="travaux" />} />
        <Route path="/entretien-logement-brest" element={<ServicePage slug="entretien" key="entretien" />} />
        <Route path="/conciergerie-militaires-brest" element={<ServicePage slug="militaires" key="militaires" />} />
        <Route path="/devis" element={<DemandePage type="devis" key="devis" />} />
        <Route path="/rendez-vous" element={<DemandePage type="rdv" key="rdv" />} />
        <Route path="/contact" element={<DemandePage type="contact" key="contact" />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/cgv" element={<Cgv />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/retractation" element={<LegalRequest mode="retractation" key="retractation" />} />
        <Route path="/resiliation" element={<LegalRequest mode="resiliation" key="resiliation" />} />
      </Route>
      <Route path="/connexion" element={<Login />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/paiement/succes" element={<PaymentSuccess />} />
      <Route path="/paiement/annule" element={<PaymentCancel />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AppLayout items={ADMIN_NAV} label="Administration" admin /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="calendrier" element={<Calendrier />} />
        <Route path="clients" element={<Clients />} />
        <Route path="logements" element={<Logements />} />
        <Route path="interventions" element={<Interventions />} />
        <Route path="chantiers" element={<Chantiers />} />
        <Route path="equipes" element={<Equipes />} />
        <Route path="devis" element={<Commercial kind="devis" key="devis" />} />
        <Route path="factures" element={<Commercial kind="factures" key="factures" />} />
        <Route path="abonnements" element={<Abonnements />} />
        <Route path="formules" element={<AdminFormules />} />
        <Route path="demandes" element={<Demandes />} />
        <Route path="documents" element={<Documents />} />
        <Route path="parametres" element={<Parametres />} />
      </Route>
      <Route path="/espace-client" element={<ProtectedRoute role="client"><AppLayout items={CLIENT_NAV} label="Espace client" /></ProtectedRoute>}>
        <Route index element={<ClientHome />} />
        <Route path="interventions" element={<ClientInterventions />} />
        <Route path="factures" element={<ClientFactures />} />
        <Route path="documents" element={<ClientDocuments />} />
        <Route path="messages" element={<ClientMessages />} />
        <Route path="compte" element={<ClientCompte />} />
      </Route>
      <Route path="/equipe" element={<ProtectedRoute role="employe"><AppLayout items={EQUIPE_NAV} label="Application équipe" /></ProtectedRoute>}>
        <Route index element={<EquipeInterventions />} />
        <Route path="chantiers" element={<EquipeChantiers />} />
        <Route path="compte" element={<ClientCompte />} />
      </Route>
      <Route path="*" element={<SiteLayout />}>
        <Route path="*" element={<RedirectOr><NotFound /></RedirectOr>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OctobreRose />
          <Suspense fallback={<p className="p-8 text-center text-sm text-slate-600" role="status" data-testid="page-loading">Chargement de votre espace…</p>}><AppRouter /></Suspense>
          <CookieBanner />
          <Toaster position="bottom-right" richColors theme="light" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
